import { z } from "zod";
import {
  PlanningOpaqueIdSchema,
  PlanningRequestIdSchema,
  TaskStateSchema,
  TodayTaskSchema,
} from "./planning";

export const TaskChecklistItemSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    task_id: PlanningOpaqueIdSchema,
    text: z.string().min(1).max(500),
    child_copy: z.string().min(1).max(300),
    sort_key: z.string().min(1).max(200),
    is_done: z.boolean(),
    done_actor_type: z.enum(["adult", "child", "system", "agent"]).nullable(),
    done_at: z.number().int().nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const TaskDependencySchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    task_id: PlanningOpaqueIdSchema,
    depends_on_task: z
      .object({
        id: PlanningOpaqueIdSchema,
        title: z.string().min(1).max(240),
        child_copy: z.string().min(1).max(160),
        status: TaskStateSchema,
        version: z.number().int().min(1),
      })
      .strict(),
    dependency_type: z.literal("blocks"),
    is_satisfied: z.boolean(),
    override: z
      .object({
        reason: z.string().min(1).max(500),
        overridden_by: z
          .object({
            id: PlanningOpaqueIdSchema,
            display_name: z.string().min(1).max(80),
          })
          .strict(),
        overridden_at: z.number().int(),
      })
      .strict()
      .nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const TaskStructureSchema = z
  .object({
    item: TodayTaskSchema,
    checklist: z.array(TaskChecklistItemSchema).max(200),
    dependencies: z.array(TaskDependencySchema).max(100),
    can_start: z.boolean(),
  })
  .strict();

export const TaskStructureResponseSchema = z
  .object({ structure: TaskStructureSchema, request_id: PlanningRequestIdSchema })
  .strict();

export const CreateTaskChecklistItemRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    text: z.string().trim().min(1).max(500),
    child_copy: z.string().trim().min(1).max(300),
  })
  .strict();

export const UpdateTaskChecklistItemRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
    text: z.string().trim().min(1).max(500),
    child_copy: z.string().trim().min(1).max(300),
  })
  .strict();

export const TaskChecklistItemCommandRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
  })
  .strict();

export const TaskChecklistItemMutationResponseSchema = z
  .object({
    item: TaskChecklistItemSchema,
    removed: z.boolean(),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const CreateTaskDependencyRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    depends_on_task_id: PlanningOpaqueIdSchema,
  })
  .strict();

export const RemoveTaskDependencyRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
  })
  .strict();

export const OverrideTaskDependencyRequestSchema = z
  .object({
    assignment_id: PlanningOpaqueIdSchema,
    expected_version: z.number().int().min(1),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export const TaskDependencyMutationResponseSchema = z
  .object({
    dependency: TaskDependencySchema,
    removed: z.boolean(),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export type TaskChecklistItem = z.infer<typeof TaskChecklistItemSchema>;
export type TaskDependency = z.infer<typeof TaskDependencySchema>;
export type TaskStructure = z.infer<typeof TaskStructureSchema>;
